/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import moment from "moment";
import { useDispatch } from "react-redux";
import dayjs from 'dayjs';
import { Alert, Checkbox, FormControlLabel } from "@mui/material";
import Functions from "../../../Helpers/Functions";
import { SET_COURSE_DECLARATION_INFO } from "../../../Redux/Reducers/DeclarationCourseReducers";
import DurationFields from "../../../Components/Fields/DurationFields";
import TimeFields from "../../../Components/Fields/TimeFields";
import FormDate from "../../../Components/FormFields/FormDate";
import FormInput from "../../../Components/Ui/FormInput";
import SessionList, {
    SessionRow,
    createEmptySessionRow,
    sessionEnd,
    sessionStart,
    sessionsTotalHours,
    validateSessionRows,
} from "../SessionList";
import BlockingPopup from "../../../Components/BlockingPopup";
import { describeExternalOverlap, fetchExternalOverlap, punctualSessionRow } from "../sessionConflicts";
import { capitalizeFirstLetter, displayError, displayHeadInfoDeclaration, getMinute, validateDateTime, validateTerms, valideDateBeforeAddClient } from "../../../Utils/Function";
import DialogLayout from "../../../Components/DialogLayout";
import { BrandContent, useTailwindBrand } from "../../../Components/shared";
import { getActiveClientBrandConfig, getActivePrestationBrandConfig } from "../../../config/environment.config";
import { SHOW_ERROR } from "../../../Redux/Reducers/MessageReducers";
import IndexStep from "../IndexStep";
import { useDeclarationContexte } from "../../../Contexts/DeclarationContexte";
import { VT_MONTHLY_CEILING_TTC, isVerifiedTrainer, vtTtcFromNet } from "../../../Utils/VerifiedTrainerAmounts";
import { describeMonthlyQuota, monthlyQuotaOf } from "../../../Utils/monthlyQuota";



const SecondStep = () => {
    moment.locale("fr");
    const { handleChangeStep, generalStep , teachr} = useDeclarationContexte();
    const dispatch = useDispatch()
    const { classes, common, brandConfig } = useTailwindBrand()
    const secondStep = generalStep?.secondStep
    const courseType = generalStep?.cours
    const child = generalStep?.firstStep?.child
    const today = new Date();
    const startAt = new Date(generalStep?.firstStep?.dateTimeTdpMoment);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const todayHour20 = moment(today).format("YYYY-MM-DD HH:mm:ss");
    const lastDayHour20 = moment(lastDayOfMonth).format("YYYY-MM-DD 20:00:00");
    const disabledBtn = courseType && (todayHour20 >= lastDayHour20)
    const start = moment().add(0.2, "hours");
    const end = moment().add(1.7, "hours");
    const now = moment().format("YYYY-MM-DD")
    const coursesThisMonth = child?.coursesThisRollingMonth ?? 0;
    // Compteur partagé : reste sur 8 tous profs confondus, et part du prof connecté
    const quotaText = describeMonthlyQuota(monthlyQuotaOf(coursesThisMonth, child?.coursesMonthByThisTeachr));
    const defaultFees = parseFloat(teachr?.defaultFees ?? '0') || 0
    const netMultiplier = 1 - defaultFees;
    // Verified Trainer : montant déjà déclaré et plafond en prix total TTC (l'API renvoie un net)
    const isVt = isVerifiedTrainer();
    const amountThisMonth = isVt ? vtTtcFromNet(child?.amount, teachr) : parseFloat(child?.amount ?? '0') * netMultiplier;
    const maxNet = (isVt ? VT_MONTHLY_CEILING_TTC : 1980 * netMultiplier).toFixed(2);
    const currentMonthDisplayName = capitalizeFirstLetter(moment().locale('fr').format('MMMM'));
    const childDisplayName = `${capitalizeFirstLetter(child?.childFirstName ?? '')} ${(child?.childLastName ?? '').toUpperCase()}`.trim();
    const defaultResult = [
        secondStep && secondStep?.startDatetimeMore ? new Date(secondStep?.startDatetimeMore) : new Date(moment().startOf('month').format("YYYY-MM-DD HH:mm:ss")),
        secondStep && secondStep?.endDatetimeMore ? new Date(secondStep?.endDatetimeMore) : new Date(moment().format("YYYY-MM-DD HH:mm:ss"))
    ]
    let minute = start.minute();
    let minuteEnd = end.minute();

    const heureAvecMinute = moment(start).minute(getMinute(minute));
    const heureAvecMinuteEnd = moment(end).minute(getMinute(minuteEnd));
    const [date, setDate] = useState<any>(secondStep ? secondStep?.date : today);
    const [duration, setDuration] = useState(secondStep ? (courseType ? (secondStep?.durationMore ? secondStep?.durationMore : 5) : (secondStep?.duration ? secondStep?.duration : 1.5)) : (courseType ? 5 : 1.5));
    const [startTime, setStartTime] = useState<any>(secondStep && secondStep?.startDatetime ? new Date(secondStep?.startDatetime) : heureAvecMinute.toDate());
    const [endTime, setEndTime] = useState(secondStep && secondStep?.endDatetime ? new Date(secondStep?.endDatetime) : heureAvecMinuteEnd.toDate());
    // P1 – séances détaillées du cours mensuel (une ligne par séance effectuée)
    const [sessions, setSessions] = useState<SessionRow[]>(
        secondStep?.sessions && Array.isArray(secondStep.sessions) && secondStep.sessions.length > 0
            ? secondStep.sessions
            // deux séances minimum pour « Plusieurs cours »
            : [createEmptySessionRow(), createEmptySessionRow()]
    );
    const [blockingSessions, setBlockingSessions] = useState<{ open: boolean; message: string; title?: string; actionLabel?: string }>({ open: false, message: "" });
    const [notes, setNotes] = useState(secondStep?.note ? secondStep?.note : "");
    const [errorEndTime, setErrorEndTime] = useState({ endTime: false });
    const [errorDates, setErrorDates] = useState({ dates: false });
    const [errorTerms, setErrorTerms] = useState({ terms: false });
    const [msgErrorEndTime, setMsgErrorEndTime] = useState({ endTime: "" })
    const [msgErrorDates, setMsgErrorDates] = useState({ dates: "" })
    const [errorMonth, setErrorMonth] = useState({
        error: false,
        msg: ""
    })

    const [isOver, setIsOver] = useState(secondStep && secondStep?.isOver ? secondStep?.isOver : false);

    // Règle « 2 h 30 après la validation du compte » : l'heure saisie dans le
    // sélecteur porte la date du jour, pas celle du cours. Toute comparaison
    // se fait donc sur la date choisie + l'heure, arrondies à la minute (la
    // date de validation porte des secondes, une ligne de séance non).
    const activationThreshold = moment(startAt).isValid()
        ? moment(startAt).add(2.5, 'hours').startOf('minute')
        : null;
    const combineDateAndTime = (dateValue: any, timeValue: any) => {
        const day = dateValue && dayjs.isDayjs(dateValue) ? moment(dateValue.format('YYYY-MM-DD'), 'YYYY-MM-DD') : moment(dateValue);
        const time = moment(timeValue);
        return day.set('hour', time.hours()).set('minute', time.minutes()).startOf('minute');
    };
    const isBeforeActivationThreshold = (start: moment.Moment) =>
        activationThreshold !== null && start.clone().startOf('minute').isBefore(activationThreshold);
    const activationErrorMessage = (what: string) =>
        `Le compte du client a été validé le ${moment(startAt).format("DD/MM/YYYY")} à ${moment(startAt).format("HH")}h${moment(startAt).format("mm")}. ${what} ne peut pas commencer moins de 2 h 30 après cette validation. Merci de modifier la date ou l'heure de début.`;
    const dateCondition = moment(startAt).isValid() && combineDateAndTime(date, startTime).isBefore(moment(startAt).startOf('minute'))

    const [openIsOver, setOpenIsOver] = useState(false);
    const [terms, setTerms] = React.useState(false);
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTerms(event.target.checked);
    };
 

    const getDate = (value: string) => {
        if (value !== "") {
            let dateCourse = value && dayjs.isDayjs(value) ? moment(value.format('YYYY-MM-DD')) : moment(value)

            const basicMonthError = valideDateBeforeAddClient(dateCourse, startAt);
            const isCurrentMonthSelected = dateCourse.format('YYYY-MM') === moment().format('YYYY-MM');
            if (basicMonthError.error) {
                setErrorMonth({ error: true, msg: basicMonthError.message });
            } else if (!courseType && isCurrentMonthSelected && coursesThisMonth >= 8) {
                setErrorMonth({
                    error: true,
                    msg: `Quota de 8 cours atteint pour ${currentMonthDisplayName}. Veuillez sélectionner une date dans un autre mois calendaire.`
                });
            } else if (!courseType) {
                setErrorMonth({ error: false, msg: "" });
            }

            if (dateCourse.format("YYYY-MM-DD") === moment(startAt).format("YYYY-MM-DD")) {
                const startAtMinute = moment(startAt).add(2.5, 'hours').startOf('minute');
                const endAtMinute = moment(startAtMinute).add(duration, "hours")
                const startMinute = getMinute(startAtMinute.minute());
                const endMinute = getMinute(endAtMinute.minute());
                setStartTime(moment(startAtMinute).minute(startMinute).toDate());
                setEndTime(moment(endAtMinute).minute(endMinute).toDate());
            }
            setIsOver(dateCourse.format("YYYY-MM-DD") < now ? true : false);
            setDate(value)
        }
    }



    const validate = () => {
        let isEndTime = true;
        let isDateValid = true;
        let isValidHour = true
        let isTerms = true
        let erroStartTime = true
        let errorMonth = true
        let dateCourse = date && dayjs.isDayjs(date) ? moment(date.format('YYYY-MM-DD'), 'YYYY-MM-DD') : moment(date)
        const monthCondition = valideDateBeforeAddClient(dateCourse, startAt).error
        const endTimeValidation = validateDateTime(duration, startTime, endTime);

        if (!courseType && !endTimeValidation.isValid) {
            setErrorEndTime({ endTime: true });
            isEndTime = false;
        } else {
            setErrorEndTime({ endTime: false });
            isEndTime = true;
        }
        if (courseType) {
            // P1 – validation des séances détaillées du cours mensuel
            const sessionValidation = validateSessionRows(sessions);
            if (!sessionValidation.isValid) {
                setBlockingSessions({ open: true, message: sessionValidation.message });
                isDateValid = false;
            } else {
                setErrorDates({ dates: false });
                setMsgErrorDates({ dates: "" });
                isDateValid = true;

                const starts = sessions.map((row) => sessionStart(row)!);
                const minStart = moment.min(starts);
                if (valideDateBeforeAddClient(minStart.clone(), startAt).error) {
                    errorMonth = false;
                    setErrorMonth({ error: true, msg: valideDateBeforeAddClient(minStart.clone(), startAt).message });
                } else if (minStart.format('YYYY-MM') === moment().format('YYYY-MM') && coursesThisMonth >= 8) {
                    // Quota de cours pour le mois sélectionné
                    errorMonth = false;
                    setErrorMonth({
                        error: true,
                        msg: `Quota de 8 cours atteint pour ${currentMonthDisplayName}. Veuillez sélectionner une date dans un autre mois calendaire.`
                    });
                } else {
                    errorMonth = true;
                    setErrorMonth({ error: false, msg: "" });
                }

                // Seule la séance la plus tôt est concernée par la règle.
                if (isBeforeActivationThreshold(minStart)) {
                    erroStartTime = false;
                    const firstIndex = starts.findIndex((start) => start.isSame(minStart));
                    dispatch({ type: SHOW_ERROR, payload: activationErrorMessage(`La séance n°${firstIndex + 1} (${minStart.format("DD/MM/YYYY")} à ${minStart.format("HH:mm")})`) });
                }
            }
        }
        else {
            if (dateCondition) {
                setErrorDates({ dates: true });
                setMsgErrorDates({ dates: `La date de début ${getActivePrestationBrandConfig("", "des ", "de la ")} ne peut pas être antérieure à la validation du compte d’avance immédiate ${getActiveClientBrandConfig('', 'de l', 'du ')}.` });
                isDateValid = false;
            } else {
                setErrorDates({ dates: false });
                setMsgErrorDates({ dates: "" });
                isDateValid = true;
            }
            if (monthCondition) {
                errorMonth = false
                setErrorMonth((prev) => ({ ...prev, error: true }))
            } else {
                errorMonth = true
                setErrorMonth({ error: false, msg: "" })
            }
            // Quota de cours pour le mois sélectionné
            if (dateCourse.format('YYYY-MM') === moment().format('YYYY-MM') && coursesThisMonth >= 8) {
                errorMonth = false;
                setErrorMonth({
                    error: true,
                    msg: `Quota de 8 cours atteint pour ${currentMonthDisplayName}. Veuillez sélectionner une date dans un autre mois calendaire.`
                });
            }
        }

        if (!courseType && isBeforeActivationThreshold(combineDateAndTime(date, startTime))) {
            erroStartTime = false
            dispatch({ type: SHOW_ERROR, payload: activationErrorMessage(`${capitalizeFirstLetter(getActivePrestationBrandConfig("", "Le ", "La "))}`) });
        }

        if (!validateTerms(terms).isValid) {
            setErrorTerms({ terms: true })
            isTerms = false;
        } else {
            setErrorTerms({ terms: false })
            isTerms = true
        }
        setMsgErrorEndTime({ endTime: endTimeValidation.message });

        return courseType ? isDateValid && isTerms && erroStartTime && errorMonth : isEndTime && isValidHour && isTerms && isDateValid && erroStartTime && errorMonth;
    };

    const handleNextButton = async (value: number) => {
        if (!validate()) {
            return;
        }
        // Chevauchement avec un autre cours du prof, tous élèves confondus :
        // vérifié ici pour l'expliquer avant l'envoi, puis par le serveur.
        const rowsToCheck = courseType ? sessions : [punctualSessionRow(date, startTime, duration)];
        const overlap = await fetchExternalOverlap(teachr?.id, rowsToCheck);
        if (overlap) {
            setBlockingSessions({
                open: true,
                title: "Créneau déjà occupé",
                actionLabel: "Modifier l'horaire",
                message: describeExternalOverlap(overlap, rowsToCheck.length),
            });
            return;
        }
        if (isOver) {
            return setOpenIsOver(true)
        } else {
            return handleNexStep(value)
        }
    }
    const handleSubmit = () => handleNexStep(3)
    const handleClose = () => setOpenIsOver(false)
    const handleNexStep = (value: number) => {
        handleClose()
        let startDateTimeMore
        let endDateTimeMore
        let startDateTime
        let endDateTime
        let dateCourse = date && dayjs.isDayjs(date) ? moment(date.format('YYYY-MM-DD'), 'YYYY-MM-DD') : moment(date)
        // P1 – période et durée totale dérivées des séances détaillées
        let sessionsDurationMore
        if (validate()) {
            if (courseType) {
                const starts = sessions.map((row) => sessionStart(row)!);
                const ends = sessions.map((row) => sessionEnd(row)!);
                startDateTimeMore = moment.min(starts).toDate();
                endDateTimeMore = moment.max(ends).toDate();
                sessionsDurationMore = sessionsTotalHours(sessions);
            } else {
                startDateTime = dateCourse.set("hour", moment(startTime).hours()).set("minutes", moment(startTime).minutes()).toDate();
                endDateTime = dateCourse.set("hour", moment(endTime).hours()).set("minutes", moment(endTime).minutes()).toDate();
            }

            if (generalStep && generalStep?.thirdStep) {
                dispatch({
                    type: SET_COURSE_DECLARATION_INFO,
                    payload: {
                        step: value,
                        firstStep: generalStep?.firstStep,
                        secondStep: {
                            date: dateCourse.toDate().toISOString(),
                            duration: !courseType ? duration : (secondStep?.duration ? secondStep?.duration : 1.5),
                            durationMore: courseType ? sessionsDurationMore : (secondStep?.durationMore ? secondStep?.durationMore : 3),
                            startDatetime: startDateTime?.toISOString() ? startDateTime?.toISOString() : heureAvecMinute.toDate().toDateString(),
                            endDatetime: endDateTime?.toISOString() ? endDateTime?.toISOString() : heureAvecMinuteEnd.toDate().toDateString(),
                            startDatetimeMore: startDateTimeMore?.toISOString() ? startDateTimeMore?.toISOString() : defaultResult[0].toDateString(),
                            endDatetimeMore: endDateTimeMore?.toISOString() ? endDateTimeMore?.toISOString() : defaultResult[1].toDateString(),
                            sessions: courseType ? sessions : null,
                            note: notes,
                            isOver: isOver

                        },
                        thirdStep: generalStep?.thirdStep,
                        cours: courseType
                    }
                });
                handleChangeStep(value)
            } else {
                dispatch({
                    type: SET_COURSE_DECLARATION_INFO,
                    payload: {
                        step: value,
                        firstStep: generalStep?.firstStep,
                        secondStep: {
                            date: dateCourse.toDate().toISOString(),
                            duration: !courseType ? duration : (secondStep?.duration ? secondStep?.duration : 1.5),
                            durationMore: courseType ? sessionsDurationMore : (secondStep?.durationMore ? secondStep?.durationMore : 3),
                            startDatetime: startDateTime ? startDateTime.toISOString() : (secondStep?.startDatetime ? secondStep?.startDatetime : heureAvecMinute.toDate().toDateString()),
                            endDatetime: endDateTime ? endDateTime?.toISOString() : (secondStep?.endDatetime ? secondStep?.endDatetime : heureAvecMinuteEnd.toDate().toDateString()),
                            startDatetimeMore: startDateTimeMore ? startDateTimeMore.toISOString() : (secondStep?.startDatetimeMore ? secondStep?.startDatetimeMore : defaultResult[0].toDateString()),
                            endDatetimeMore: endDateTimeMore ? endDateTimeMore.toISOString() : (secondStep?.endDatetimeMore ? secondStep?.endDatetimeMore : defaultResult[1].toDateString()),
                            sessions: courseType ? sessions : null,
                            note: notes,
                            isOver: isOver
                        },
                        cours: courseType
                    }
                });
                handleChangeStep(value)
            }

        }
    }

    const displayDate = () => {
        if (courseType) {
            const starts = sessions.map((row) => sessionStart(row)).filter(Boolean) as moment.Moment[];
            const ends = sessions.map((row) => sessionEnd(row)).filter(Boolean) as moment.Moment[];
            const totalHours = sessionsTotalHours(sessions);
            if (starts.length === 0) {
                return (<>pour une durée totale de <strong>{Functions.renderDuration(totalHours * 2)}</strong></>)
            }
            return (<>
                de <strong>{sessions.length} séance{sessions.length > 1 ? "s" : ""}</strong> entre le <strong>{moment.min(starts).format("DD MMMM YYYY")}</strong> et le <strong>{moment.max(ends).format("DD MMMM YYYY")}</strong> pour une durée totale de <strong>{Functions.renderDuration(totalHours * 2)}</strong>
            </>)
        } else {
            return (<>
                le <strong>{moment(date && dayjs.isDayjs(date) ? moment(date.format('YYYY-MM-DD')) : moment(date)).format("DD MMMM YYYY")}</strong>  de  <strong>{moment(startTime).format("HH:mm")}</strong> à <strong>{moment(endTime).format("HH:mm")}</strong> pour une durée de <strong>{Functions.renderDuration(duration * 2)}</strong>
            </>)
        }
    }


    return (
        <>
            {/* P1 – popup bloquant : impossible de poursuivre sans séances détaillées valides */}
            <BlockingPopup
                open={blockingSessions.open}
                title={blockingSessions.title ?? "Séances détaillées requises"}
                message={blockingSessions.message}
                actionLabel={blockingSessions.actionLabel ?? "Compléter mes séances"}
                onAction={() => setBlockingSessions({ open: false, message: "" })}
            />
            <DialogLayout
                title={`Déclaration ${getActivePrestationBrandConfig("", "d'un ", "d'une ")} déjà passé`}
                text={
                    <>
                        <BrandContent
                            tag="p"
                            children={<>
                                Vous êtes sur le point de déclarer {getActivePrestationBrandConfig("", "un ", "une ")} déjà réalisé {displayDate()}.
                                Veuillez noter qu’une fois déclaré, <strong className={`${classes.error} underline`}>{getActivePrestationBrandConfig("", "ce ", "cette ")} ne pourra plus être modifié ni supprimé</strong>.
                                Merci de fournir des informations exactes afin d’assurer un suivi conforme.
                            </>}
                            type="description"
                            className={`mb-2`}
                        />
                    </>
                }
                open={openIsOver}
                cancel='Retour'
                valid='Valider'
                handleSubmit={handleSubmit}
                handleClose={handleClose}
            />
            <IndexStep
                title={courseType ? `📚 Plusieurs ${getActivePrestationBrandConfig("s")}` : `📅 ${capitalizeFirstLetter(getActivePrestationBrandConfig(' ponctuelle', "", "", " ponctuel"))}`}
                body={
                    <>
                        {/* Bannière quota mensuel */}
                        {child && (
                            <Alert
                                severity={coursesThisMonth >= 8 ? 'warning' : 'info'}
                                variant="outlined"
                                sx={{ mb: 2, fontSize: '0.82rem', fontFamily : brandConfig.fontFamily }}
                            >
                                {courseType
                                    ? `Plusieurs ${getActivePrestationBrandConfig("s")}`
                                    : capitalizeFirstLetter(getActivePrestationBrandConfig(' ponctuelle', "un ", "une ", " ponctuel"))
                                }{' '}pour <strong>{childDisplayName || `votre ${getActiveClientBrandConfig()}`}</strong> au mois de <strong>{currentMonthDisplayName}</strong> :
                                <ul style={{ margin: '4px 0 2px', paddingLeft: 20 }}>
                                    <li>
                                        {capitalizeFirstLetter(getActivePrestationBrandConfig("s"))} ce mois : <strong style={{ color: quotaText.tone === 'danger' ? '#d32f2f' : quotaText.tone === 'warning' ? '#ed6c02' : 'inherit' }}>{quotaText.headline}</strong>
                                        {' '}({quotaText.detail})
                                    </li>
                                    <li>
                                        Heures déclarées : <strong>{child?.duration === 0 ? '0h' : (Functions.renderDuration(child?.duration) || '0h')}</strong>
                                    </li>
                                    <li>
                                        Montant déclaré : <strong>{amountThisMonth.toFixed(2)}€</strong> / {maxNet} €
                                    </li>
                                </ul>
                                {coursesThisMonth >= 8
                                    ? <>Quota de cours atteint ce mois. Vous pouvez déclarer sur un autre mois calendaire.</>
                                    : <>Maximum : 8 cours et {maxNet} € {isVt ? "TTC par client" : "par élève"} par mois.</>
                                }
                            </Alert>
                        )}
                        {/* <BrandContent
                            tag="p"
                            children={displayHeadInfoDeclaration(child, classes, courseType, duration)}
                            type="description"
                            className={`mb-4`}
                        /> */}
                        <BrandContent
                            tag="div"
                            children={<>
                                {(courseType && moment(today).format("YYYY-MM-DD") === moment(lastDayOfMonth).format("YYYY-MM-DD")) &&
                                    <Alert variant="outlined" severity="info" sx={{fontFamily : brandConfig.fontFamily}}>
                                        Aujourd’hui, dernier jour du mois, est la date limite pour déclarer vos prestations du mois en cours.
                                        Vous avez jusqu’à 20h ce jour pour déclarer vos {getActivePrestationBrandConfig("s")} du mois. Une fois ce mois-ci terminé, cette période de déclaration sera fermée et vous pourrez alors déclarer vos {getActivePrestationBrandConfig("s")} sur la période du mois suivant.
                                    </Alert>
                                }
                            </>}
                            type="description"
                            className={`mb-4`}
                        />
                        <BrandContent
                            tag="div"
                            children={<>
                                {
                                    courseType ?
                                        <div className="lg:col-span-2 w-full">
                                            {/* P1 – saisie détaillée des séances du mois */}
                                            <SessionList
                                                rows={sessions}
                                                onChange={setSessions}
                                                startAt={generalStep?.firstStep?.dateTimeTdpMoment}
                                                setIsOver={setIsOver}
                                            />
                                        </div> :
                                        <>
                                        <DurationFields
                                            customDuration={""}
                                            value={Functions.renderDuration(duration * 2)}
                                            isPunctualMonth={courseType}
                                            duration={duration}
                                            setDuration={setDuration}
                                            setEndTime={setEndTime}
                                            startTime={startTime}
                                            label={`Durée de ${getActivePrestationBrandConfig()}`}
                                        />
                                            <FormDate
                                                defaultValue={date}
                                                callback={getDate}
                                                label={`Jour ${getActivePrestationBrandConfig("", "du ", "de la")}`}
                                                error={errorDates.dates ? msgErrorDates.dates : undefined}
                                                minDate={moment(startAt).format("YYYY-MM-DD")}
                                            />
                                            <TimeFields
                                                value={startTime}
                                                endValue={endTime}
                                                date={date}
                                                setEndTime={setEndTime}
                                                setStartTime={setStartTime}
                                                duration={duration}
                                                setIsOver={setIsOver}
                                                startAt={startAt}
                                                label={`Heure de début des ${getActivePrestationBrandConfig("s")}`}
                                            />
                                            <FormInput
                                                label={`Heure de fin des ${getActivePrestationBrandConfig("s")}`}
                                                value={moment(endTime).format('HH:mm')}
                                                error={errorEndTime.endTime ? msgErrorEndTime.endTime : undefined}
                                                readOnly={true}
                                            />
                                            {/* <div className={`relative cursor-not-allowed px-4 py-1 rounded-lg ${classes.input} shadow-dashboard-setup-input h-max`}>
                                                <label className='block lg:text-base cursor-not-allowed font-bold'>Heure de fin des {getActivePrestationBrandConfig("s")}</label>
                                                <input disabled value={moment(endTime).format('HH:mm')} className={`w-full lg:text-base bg-transparent cursor-not-allowed placeholder:text-grey`} type="text" />
                                            </div>
                                            <div className="col-span-2 w-full">
                                                {displayError(errorEndTime.endTime, msgErrorEndTime.endTime, classes)}
                                            </div> */}
                                        </>
                                }
                                {courseType &&
                                    <div className="lg:col-span-2 w-full">
                                        <FormInput
                                            label={`Note optionnelle à destination de votre ${getActiveClientBrandConfig()}`}
                                            textarea={true}
                                            value={notes}
                                            onChangeText={(e) => setNotes(e.target.value)}
                                            placeholder={`Vous pouvez renseigner ici toute information utile que vous souhaitez partager concernant la facturation des ${getActivePrestationBrandConfig("s")} du mois`}
                                        />
                                    </div>
                                }
                                {(courseType && (todayHour20 >= lastDayHour20)) && <Alert variant="outlined" severity="warning" className="font-nunito">
                                    Attention /!\ Nous sommes le
                                    dernier jour du mois et il est 20h passé, il n'est plus possible de
                                    déclarer vos {getActivePrestationBrandConfig("s")} pour le mois en cours. Veuillez attendre jusqu'au
                                    1er du mois prochain pour déclarer vos {getActivePrestationBrandConfig("s")}.
                                </Alert>}
                                <BrandContent
                                    tag="div"
                                    children={<>
                                        <FormControlLabel className={`items-start gap-2 ${classes.fontFamily} ${common.body}`}
                                            label={<p className={`${classes.fontFamily} ${common.body}`}>Je m'engage à ce que cette prestation soit réalisée au domicile de mon {getActiveClientBrandConfig()} conformément à
                                                l'article 199 sexdecies du Code général des impôts (CGI) en
                                                France. Je m'engage également à ce que cette prestation soit
                                                réalisée aux conditions d'heures et de tarifs définies ci-après.</p>}
                                            control={
                                                <Checkbox
                                                    checked={terms}
                                                    onChange={handleChange}
                                                    inputProps={{ 'aria-label': 'controlled' }}
                                                    className={`p-0 ${classes.primaryText} `}
                                                />}
                                            sx={{
                                                color: brandConfig.colors.text,
                                                '& .MuiFormControlLabel-label': {
                                                    'textAlign': 'justify',
                                                    'color': brandConfig.colors.text,
                                                }
                                            }}
                                        />
                                        {displayError(errorTerms.terms, validateTerms(terms).message, classes)}
                                        {displayError(errorMonth.error, errorMonth.msg, classes)}
                                    </>}
                                    type="description"
                                    className={`w-full space-y-2 font-bold lg:col-span-2`}
                                />
                            </>}
                            type="description"
                            className={`lg:grid flex flex-col gap-y-4 lg:grid-cols-2 grid-cols-1 lg:gap-4 lg:gap-y-6`}
                        />
                    </>
                }
                handleChangeStep={handleChangeStep}
                handleNexStep={handleNextButton}
                value={3}
                step={2}
                generalStep={generalStep}
                disabled={disabledBtn}
            />
        </>
    );
};

export default SecondStep;



